<template>
  <div>
    <b-overlay
      :show="isLoading"
      rounded="sm"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      @shown="onShown"
      @hidden="onHidden"
    >
      <section
        class="invoice-preview-wrapper"
        style="position: relative"
      >
        <!-- Alert: No item found -->
        <b-alert
          variant="danger"
          :show="order === undefined"
        >
          <h4 class="alert-heading">
            Error fetching invoice data
          </h4>
          <div class="alert-body">
            No invoice found with this invoice id. Check
            <b-link
              class="alert-link"
              :to="{ name: 'facture-list' }"
            >
              invoice List
            </b-link>
            for other invoice.
          </div>
        </b-alert>
        <div
          v-if="isLoading === true"
          class="text-center mb-2"
        >
          <b-spinner
            variant="primary"
            label="Text Centered"
          />
        </div>
        <b-row
          v-if="allOrders && allOrders.length > 0"
          class="invoice-preview"
        >

          <!-- Col: Left (Invoice Container) -->
          <b-col
            cols="12"
            xl="9"
            md="8"
          >
            <b-card
              v-for="orderItem in allOrders"
              :id="orderItem.id"
              :key="orderItem.id"
              no-body
              class="invoice-preview-card pagebreak"
              :class="printDensityClass(orderItem)"
            >
              <div
                class="d-flex justify-content-between flex-wrap mr-1 ml-1 delivery-note-header"
              >

                <div class="invoice-preview">
                  <h1
                    class="mt-1 dn-title"
                    style="color: #0A2634; font-size: 30px"
                    aria-label="En-tête du bon de livraison"
                  >
                    Bon de livraison
                    <span
                      class="invoice-title invoice-number"
                      style="color: #0A2634;"
                    >
                      N° : BL-{{ orderItem.id }}
                    </span>
                  </h1>
                  <h6
                    class="dn-date"
                    style="color: black "
                  >
                    Date :
                    <span
                      class="invoice-date"
                      style="color: black"
                    >
                      {{ formatFn(orderItem.created_at) }}</span>
                  </h6>
                  <div class="mt-1">
                    <div class="mt-1">
                      <b-card-text
                        class="h4"
                        style="color: black"
                      >
                        <strong style="color:#0A2634">{{ companyBordereauValue('name', company.name) }} :</strong> {{ company.tagline || 'Société de livraison des colis' }}
                      </b-card-text>
                      <b-card-text
                        class="h4"
                        style="color: black"
                      >
                        <strong style="color:#0A2634">Matricule fiscale :</strong> {{ companyBordereauValue('tax_identification_number', fallbackCompanyTaxId) }}
                      </b-card-text>
                      <b-card-text
                        class="h4"
                        style="color: black"
                      >
                        <strong style="color:#0A2634">Adresse :</strong> {{ companyBordereauValue('address', company.address) }}
                      </b-card-text>
                      <b-card-text
                        class="h4"
                        style="color: black"
                      >
                        <strong style="color:#0A2634">Tel :</strong> {{ companyBordereauValue('phone_number', companyPhoneValue) }}
                      </b-card-text>
                    </div>
                  </div>
                </div>

                <div class="dn-header-logo-center">
                  <brand-logo
                    height="auto"
                    class="logo-top"
                  />
                </div>

                <div class="dn-header-codes-right">
                  <div class="d-flex justify-content-end align-items-start mt-0 codes-right">
                    <div class="header-qr-wrap">
                      <qrcode-vue
                        :value="headerBarcodeValue(orderItem)"
                        :size="headerQrSize"
                        level="H"
                      />
                      <span class="header-qr-id" aria-label="Identifiant code-barres">{{ headerBarcodeValue(orderItem) }}</span>
                    </div>
                    <div class="header-barcode-wrap">
                      <barcode
                        :value="headerBarcodeValue(orderItem)"
                        :height="78"
                        :width="2.25"
                        :font-size="28"
                        font-options="bold"
                        :text-margin="2"
                        :margin="0"
                        :display-value="true"
                        class="barcode-small"
                        aria-label="Code-barres de la commande"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <b-card-text
                v-if="isFlagEnabled(orderItem.is_an_exchange)"
                class="status-banner status-banner--exchange"
                aria-label="Colis en échange"
              >
                <strong>Échange</strong>
              </b-card-text>
              <b-card-text
                v-if="isFlagEnabled(orderItem.is_fragile)"
                class="status-banner status-banner--fragile"
                aria-label="Colis fragile"
              >
                <strong>Fragile</strong>
              </b-card-text>
              <div
                v-if="orderItem.repository"
                class="dn-agence-line mr-1 ml-1 mb-1"
              >
                <strong>{{ orderItem.repository.name }}</strong>
                &nbsp;&gt;&gt;------- dispatch -------&gt;&gt;&nbsp;
                <strong>{{ dispatchGovernorateName(orderItem) }}</strong>
              </div>
              <div class="d-flex flex-row flex-wrap justify-content-between dn-info-row mr-1 ml-1 mb-1">

                <div class="dn-info-col dn-info-col--client">

                  <div
                    class="client-info-box"
                    style="border: 2px solid black; font-size: 18px; min-width: 380px;"
                  >
                    <b-card-text class="ml-2 mr-2 mt-2 mb-1 font-weight-bold">
                      <span
                        style="font-weight:900; color: #1D1D1B"
                      >Client : </span> {{ orderItem.name }}
                    </b-card-text>
                    <b-card-text class="ml-2 mr-2 mb-1">
                      <span
                        style="font-weight:900; color: #1D1D1B"
                      >Gouvernorat : </span>{{ typeof orderItem.governorate === 'object' ? orderItem.governorate.name : orderItem.governorate }}
                      <span v-if="orderItem.delegation"> , {{ typeof orderItem.delegation === 'object' ? orderItem.delegation.name : orderItem.delegation }}</span>
                    </b-card-text>

                    <b-card-text class="ml-2 mr-2 mb-1">
                      <span style="font-weight:900; color: #1D1D1B">Adresse :</span>
                      {{ orderItem.address }}
                    </b-card-text>
                    <b-card-text class="ml-2 mr-2 mb-1">
                      <span
                        style="font-weight:900; color: #1D1D1B"
                      > téléphone :</span>
                      {{ orderItem.phone1 }}<span v-if="orderItem.phone2"> / {{ orderItem.phone2 }}</span>
                    </b-card-text>
                  </div>
                </div>
                <div class="dn-info-col dn-info-col--supplier">

                  <div
                    class="supplier-info-box"
                    style="border: 2px solid black; font-size: 18px; min-width: 250px;"
                  >

                    <b-card-text class="ml-2 mr-2 mt-2 mb-1 font-weight-bold">
                      <span
                        style="font-weight:900; color: #EA2F2F"
                      >Fournisseur :</span> {{ supplierBordereauValue(orderItem.supplier, 'username', orderItem.supplier.page_name || orderItem.supplier.username) }}
                      <span
                        v-if="orderItem.supplier.is_external"
                        class="badge badge-info ml-1"
                        style="font-size: 10px;"
                      >{{ orderItem.supplier.source_company }}</span>
                    </b-card-text>
                    <b-card-text class="ml-2 mr-2 mb-1">
                      <span
                        style="font-weight:900; color: #EA2F2F"
                      >Téléphone : </span>{{ supplierBordereauValue(orderItem.supplier, 'phone_number', supplierPhoneValue(orderItem.supplier)) }}
                    </b-card-text>
                    <b-card-text class="ml-2 mr-2 mb-1">
                      <span
                        style="font-weight:900; color: #EA2F2F"
                      >Adresse :</span>
                      {{ supplierBordereauValue(orderItem.supplier, 'address', orderItem.supplier.address) }}
                    </b-card-text>
                    <b-card-text
                      v-if="orderItem.supplier.external_barcode"
                      class="ml-2 mr-2 mb-1"
                    >
                      <span style="font-weight:900; color: #EA2F2F">Code-barres :</span>
                      {{ orderItem.supplier.external_barcode }}
                    </b-card-text>
                    <b-card-text
                      v-if="supplierIdentityField(orderItem.supplier)"
                      class="ml-2 mr-2 mb-1"
                    >
                      <span style="font-weight:900; color: #EA2F2F">{{ supplierIdentityField(orderItem.supplier).label }} :</span>
                      {{ supplierIdentityField(orderItem.supplier).value }}
                    </b-card-text>
                  </div>
                </div>

              </div>

              <!-- Invoice Description: Table -->
              <div class="mr-1 ml-1 delivery-note-table">
                <b-table-lite
                  class="ta"
                  style="font-size: 20px; border: 2px solid black"
                  responsive
                  :items="orderItem.p"
                  :fields="['désignation', 'quantité']"
                >
                  <template #cell(taskDescription)="data">
                    <b-card-text class="font-weight-bold mb-25">
                      {{ data.item.taskTitle }}
                    </b-card-text>
                    <b-card-text class="text-nowrap">
                      {{ data.item.taskDescription }}
                    </b-card-text>
                  </template>
                </b-table-lite>
              </div>

              <!-- Price + Commentaire on same row right after table -->
              <div class="mr-1 ml-1 dn-after-table-row">
                <div class="dn-after-table-left">
                  <h1 class="dn-comment-line">
                    Commentaire :
                    <span class="dn-comment-text">{{ orderItem.note }}</span>
                  </h1>
                  <div class="dn-flags-list">
                    <b-card-text
                      v-if="isFlagEnabled(orderItem.is_an_exchange)"
                      class="h4"
                      style="color:black"
                    >
                      <feather-icon
                        icon="RefreshCwIcon"
                        size="18"
                        class="mr-50"
                        style="color: #5A8DEE;"
                      />
                      <strong style="color:#EA2F2F">Échange :</strong> OUI
                    </b-card-text>
                    <b-card-text
                      v-if="isFlagEnabled(orderItem.is_fragile)"
                      class="h4"
                      style="color:black"
                    >
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="18"
                        class="mr-50"
                        style="color: #FDAC41;"
                      />
                      <strong style="color:#EA2F2F">Fragile :</strong> OUI
                    </b-card-text>
                    <b-card-text
                      v-if="isFlagEnabled(orderItem.is_discharge)"
                      class="h4"
                      style="color:black"
                    >
                      <feather-icon
                        icon="FileTextIcon"
                        size="18"
                        class="mr-50"
                        style="color: #A3A1FB;"
                      />
                      <strong style="color:#EA2F2F">Décharge :</strong> OUI
                    </b-card-text>
                    <b-card-text
                      class="h4"
                      style="color:black"
                    >
                      <feather-icon
                        icon="PackageIcon"
                        size="18"
                        class="mr-50"
                        style="color: #C59C6C;"
                      />
                      <strong style="color:#EA2F2F">ouvrir colis :</strong> {{ isFlagEnabled(orderItem.can_open_package) ? 'OUI' : 'NON' }}
                    </b-card-text>
                  </div>
                </div>
                <div class="dn-after-table-right">
                  <div class="dn-total-box">
                    <table style="border: 2px solid #0A2634">
                      <tr><th class="text-center dn-price-ttc" style="background-color:#fff; color:#0A2634;">
                        Total TTC: {{ orderItem.price_ttc }} DT
                      </th></tr>
                    </table>
                  </div>
                </div>
              </div>

              <!-- QR code at bottom -->
              <div
                v-if="footerBarcodeValue(orderItem) || orderItem.external_id_2"
                class="mr-1 ml-1 dn-qr-section"
              >
                <div class="footer-codes-wrap">
                  <template v-if="footerBarcodeValue(orderItem)">
                    <div class="footer-qr-wrap">
                      <qrcode-vue
                        :value="footerBarcodeValue(orderItem)"
                        :size="footerQrSize"
                        class="footer-qr"
                        level="H"
                      />
                    </div>
                    <div class="footer-barcode-wrap">
                      <barcode
                        :value="footerBarcodeValue(orderItem)"
                        :height="78"
                        :width="1.5"
                        :font-size="22"
                        font-options="bold"
                        :text-margin="3"
                        :margin="0"
                        :display-value="true"
                        class="footer-barcode"
                        aria-label="Code-barres externe du bon de livraison"
                      />
                    </div>
                  </template>
                  <div
                    v-if="orderItem.external_id_2"
                    class="footer-barcode-wrap footer-barcode-wrap--ext2"
                  >
                    <barcode
                      :value="(orderItem.external_barcode_2 || orderItem.external_id_2).toString()"
                      :height="78"
                      :width="1.5"
                      :font-size="22"
                      font-options="bold"
                      :text-margin="3"
                      :margin="0"
                      :display-value="true"
                      class="footer-barcode"
                      aria-label="Code-barres externe 2"
                    />
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>

          <!-- Right Col: Card -->
          <b-col
            cols="12"
            md="4"
            xl="3"
            class="invoice-actions"
          >
            <b-card>
              <!-- Button: Print -->
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                :disabled="isLoading"
                variant="outline-secondary"
                class="mb-75"
                block
                @click="printInvoice"
              >
                Imprimer
              </b-button>

              <!-- Button: Edit -->
            </b-card>
          </b-col>
        </b-row>
      </section>
      <template #overlay>
        <div class="text-center">
          <b-icon
            icon="stopwatch"
            font-scale="3"
            animation="cylon"
          />
          <p id="cancel-label">
            Veuillez patienter...
          </p>
          Génération des bon des livraison en cours
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTableLite,
  BCardText,
  BButton,
  BAlert,
  BLink,
  VBToggle,
  BSpinner,
  BOverlay,
  BIcon,
} from 'bootstrap-vue'
import QrcodeVue from 'qrcode.vue'
import VueBarcode from 'vue-barcode'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
    QrcodeVue,
    barcode: VueBarcode,
    BSpinner,
    BOverlay,
    BIcon,
  },
  // eslint-disable-next-line vue/require-prop-types
  props: ['orderIds'],
  data() {
    return {
      size: 80,
      headerQrSize: 96,
      footerQrSize: 104,
      p: [],
      order: [],
      allOrders: [],
      company: {},
      isLoading: false,
      show: false,
    }
  },

  async created() {
    await this.loadCompanyInfo()
    this.getOrders()
  },
  computed: {
    companyPhoneValue() {
      if (!this.company) return ''
      return [this.company.phone1, this.company.phone2].filter(Boolean).join(' / ')
    },
    fallbackCompanyTaxId() {
      const taxId = this.company && this.company.tax_identification_number
      return taxId ? `****${String(taxId).slice(4)}` : ''
    },
  },
  methods: {
    async loadCompanyInfo() {
      const company = await storeAuth.dispatch('fetchCompanyInfo')
      this.company = company || storeAuth.state.company || {}
    },

    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)

      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')}`
    },

    formatDate(date) {
      if (!date) {
        return null
      }
      const [year] = date.split('-')
      return `${year.slice(2, 4)}`
    },

    addnewitem(qte, nom) {
      this.p.push({
        désignation: nom,
        quantité: qte,
      })
    },
    locationName(value) {
      if (!value) {
        return ''
      }

      if (typeof value === 'object') {
        return value.name || ''
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim()

        if (!trimmedValue) {
          return ''
        }

        const matches = trimmedValue.match(/\{.*\}/)

        if (matches && matches[0]) {
          try {
            const parsedValue = JSON.parse(matches[0])

            if (parsedValue && parsedValue.name) {
              return parsedValue.name
            }
          } catch (error) {
            return trimmedValue
          }
        }

        return trimmedValue
      }

      return ''
    },
    dispatchGovernorateName(order) {
      const governorateName = this.locationName(order ? order.governorate : null)

      if (governorateName) {
        return governorateName
      }

      if (order && order.destination_repository_name) {
        return order.destination_repository_name
      }

      if (order && order.repository && order.repository.name) {
        return order.repository.name
      }

      return ''
    },
    async getOrders() {
      this.isLoading = true
      try {
        // Parse order IDs from route parameter
        const orderIds = this.orderIds ? this.orderIds.split(',') : []

        if (orderIds.length === 0) {
          // Show error message - no orders selected
          this.isLoading = false
          return
        }

        // Make single API call to get all orders
        const response = await axios.post('/api/orders/bulk-delivery-notes/', {
          order_ids: orderIds,
        })

        if (response.data && response.data.orders) {
          this.allOrders = response.data.orders.map(order => {
            const normalizedFlags = this.normalizeOrderFlags(order)

            return {
              ...order,
              ...normalizedFlags,
              // Process products for each order (keep the existing logic)
              p: order.processed_products || [],
            }
          })

          const currentUser = storeAuth.state.user
          if (orderIds.length > 0 && currentUser && currentUser.role === 'supplier') {
            await this.markOrdersAsPrinted(orderIds)
          }
        }
      } catch (error) {
        // Error fetching orders
      } finally {
        this.isLoading = false
      }
    },
    async markOrdersAsPrinted(orderIds) {
      try {
        await axios.post('/api/orders/mark-orders-as-printed/', {
          order_ids: orderIds,
        })
      } catch (error) {
        // Error marking orders as printed
      }
    },
    onShown() {
      // Focus the cancel button when the overlay is showing
      // this.$refs.cancel.focus()
    },
    onHidden() {
      // Focus the show button when the overlay is removed
      // this.$refs.show.focus()
    },
    isFlagEnabled(value) {
      if (typeof value === 'boolean') return value
      if (typeof value === 'number') return value === 1
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'oui'
      }
      return false
    },
    firstDefined(...values) {
      for (let i = 0; i < values.length; i += 1) {
        if (values[i] !== undefined && values[i] !== null) return values[i]
      }
      return null
    },
    supplierPhoneValue(supplier) {
      if (!supplier) return ''
      return [supplier.phone, supplier.phone2].filter(Boolean).join(' / ')
    },
    companyBordereauValue(field, fallback = '') {
      const display = this.company
        && this.company.bordereau_display
        && this.company.bordereau_display[field]

      if (display && Object.prototype.hasOwnProperty.call(display, 'display_value')) {
        return display.display_value
      }

      return fallback || ''
    },
    supplierBordereauValue(supplier, field, fallback = '') {
      const display = supplier
        && supplier.bordereau_display
        && supplier.bordereau_display[field]

      if (display && Object.prototype.hasOwnProperty.call(display, 'display_value')) {
        return display.display_value
      }

      return fallback || ''
    },
    supplierBordereauDisplay(supplier, field) {
      return supplier
        && supplier.bordereau_display
        && supplier.bordereau_display[field]
    },
    supplierIdentityField(supplier) {
      if (!supplier) return null

      const fields = [
        {
          key: 'matricule_fiscale',
          label: 'Matricule Fiscale',
          fallback: supplier.tax_identification_number,
        },
        {
          key: 'cin',
          label: 'CIN',
          fallback: supplier.cin,
        },
      ]

      const hasIdentityValue = field => {
        const display = this.supplierBordereauDisplay(supplier, field.key)
        const value = display && Object.prototype.hasOwnProperty.call(display, 'value')
          ? display.value
          : field.fallback
        return value !== undefined && value !== null && String(value).trim() !== ''
      }

      const visibleField = fields.find(field => {
        const display = this.supplierBordereauDisplay(supplier, field.key)
        return display && display.show === true && hasIdentityValue(field)
      })

      const selectedField = visibleField || fields.find(field => {
        const display = this.supplierBordereauDisplay(supplier, field.key)
        return display && display.show === false && hasIdentityValue(field)
      }) || fields.find(field => hasIdentityValue(field))

      if (!selectedField) return null

      return {
        label: selectedField.label,
        value: this.supplierBordereauValue(supplier, selectedField.key, selectedField.fallback),
      }
    },
    normalizeOrderFlags(order) {
      const isAnExchange = this.firstDefined(
        order.is_an_exchange,
        order.is_exchange,
        order.exchange,
        order.an_exchange,
      )

      const isFragile = this.firstDefined(
        order.is_fragile,
        order.fragile,
      )

      const isDischarge = this.firstDefined(
        order.is_discharge,
        order.discharge,
      )

      const canOpenPackage = this.firstDefined(
        order.can_open_package,
        order.open_package,
        order.canOpenPackage,
      )

      return {
        is_an_exchange: isAnExchange,
        is_fragile: isFragile,
        is_discharge: isDischarge,
        can_open_package: canOpenPackage,
      }
    },
    normalizedBarcodeValue(value) {
      return String(value === undefined || value === null ? '' : value).trim()
    },
    deliveryNoteBarcodeMode(order) {
      const displayMode = order && order.delivery_note_barcode && order.delivery_note_barcode.mode
      if (displayMode) {
        return displayMode
      }

      const companyMode = (this.company && this.company.delivery_note_barcode_mode) || 'order_id_and_external_id'
      const isSyncRelatedOrder = Boolean(
        this.normalizedBarcodeValue(order && order.external_id)
          || (order && (order.is_outsourcing || order.outsourcing_company)),
      )
      return companyMode === 'external_id_only' && isSyncRelatedOrder
        ? companyMode
        : 'order_id_and_external_id'
    },
    headerBarcodeValue(order) {
      const display = order && order.delivery_note_barcode
      if (display && display.primary_value) {
        return this.normalizedBarcodeValue(display.primary_value)
      }

      const externalId = this.normalizedBarcodeValue(order && order.external_id)
      const orderId = this.normalizedBarcodeValue(order && order.id)
      if (this.deliveryNoteBarcodeMode(order) === 'external_id_only') {
        return externalId || orderId
      }

      return orderId
    },
    footerBarcodeValue(order) {
      const display = order && order.delivery_note_barcode
      if (display && display.secondary_value) {
        return this.normalizedBarcodeValue(display.secondary_value)
      }

      if (this.deliveryNoteBarcodeMode(order) === 'external_id_only') {
        return ''
      }

      return this.normalizedBarcodeValue(order && order.external_id)
    },
    bottomCodeValue(order) {
      return this.footerBarcodeValue(order)
    },
    printDensityClass(order) {
      if (!order) return 'dn-print-compact'

      const products = Array.isArray(order.p) ? order.p : []
      const textParts = [
        order.name,
        order.address,
        order.note,
        order.phone1,
        order.phone2,
        order.supplier && order.supplier.username,
        order.supplier && order.supplier.address,
        order.supplier && order.supplier.phone,
        order.supplier && order.supplier.phone2,
        ...products.map(product => `${product.désignation || ''} ${product.quantité || ''}`),
      ]
      const textLength = textParts
        .filter(Boolean)
        .join(' ')
        .length
      const flagCount = [
        order.is_an_exchange,
        order.is_fragile,
        order.is_discharge,
        order.can_open_package,
      ].filter(value => this.isFlagEnabled(value)).length
      const hasBottomCodes = Boolean(this.footerBarcodeValue(order))
      const densityScore = (products.length * 35) + (textLength / 14) + (flagCount * 18) + (hasBottomCodes ? 35 : 0)

      if (densityScore < 95) return 'dn-print-roomy'
      if (densityScore < 150) return 'dn-print-comfortable'
      return 'dn-print-compact'
    },
    printInvoice() {
      window.print()
    },
  },
  setup() {
    return {}
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
table,
td,
th {
  padding: 10px;
  text-align: left;
  border: 1px solid black;
}

table {
  width: 100%;
  border-collapse: collapse;
}

// ── Responsive delivery note layout ──
.invoice-preview-wrapper {
  --delivery-note-page-height: 287mm;
}

.invoice-preview-card.pagebreak {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: var(--delivery-note-page-height);
}

.delivery-note-header {
  align-items: flex-start;
}

.delivery-note-company {
  flex: 0 0 auto;
}

// ── Header right codes ──
.dn-header-right-codes {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.dn-header-codes {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 6px;
}

.dn-info-row {
  gap: 8px;
}

.dn-info-col {
  flex: 1 1 300px;
  min-width: 0;
}

.dn-info-col--client .client-info-box {
  font-size: 18px;
}

.dn-info-col--supplier .supplier-info-box {
  font-size: 18px;
}

@media (max-width: 768px) {
  .dn-info-col {
    flex: 1 1 100%;
  }

  .dn-info-col--client .client-info-box {
    font-size: 16px;
  }

  .dn-info-col--supplier .supplier-info-box {
    font-size: 12px;
    margin-left: 0 !important;
  }

  .dn-title {
    font-size: 20px !important;
  }

  .dn-date {
    font-size: 12px !important;
  }

  .dn-header-logo {
    width: 120px !important;
  }

  .company-meta .h4 {
    font-size: 11px !important;
  }

  .ta {
    font-size: 14px !important;
  }
}

.mycolor{
    background-color:#1565C0 ;
  }
.contact {
  background-color: #1565C0;
  padding: 10px;
  border-radius: 20px;
}

.delivery-note-table {
  flex: 0 0 auto;
  min-height: 0;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.delivery-note-table .table,
.delivery-note-table .table-responsive {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.dn-after-table-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0 0;
  padding: 0;
  flex: 0 0 auto;
}

.dn-after-table-left {
  flex: 1 1 auto;
  min-width: 0;
}

.dn-after-table-right {
  flex: 0 0 auto;
  align-self: flex-start;
}

.dn-qr-section {
  margin-top: auto;
  padding-bottom: 6px;
  flex: 0 0 auto;
}

.footer-codes-wrap {
  display: inline-flex;
  align-items: flex-end;
  gap: 0;
  flex-wrap: nowrap;
}

.dn-agence-line {
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  padding: 4px 8px;
  border: 1px solid #0A2634;
  color: #0A2634;
  margin-bottom: 6px;
}

.dn-comment-line {
  font-weight: bold;
  line-height: 1.2;
}

.dn-comment-text {
  color: #000;
  font-size: 18px;
  font-weight: normal;
}

.dn-flags-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.dn-total-box {
  flex: 0 0 auto;
}

.dn-total-box table {
  margin: 0;
}

.dn-price-ttc {
  font-size: 28px !important;
  font-weight: 900 !important;
  letter-spacing: 0.5px;
}

.header-barcode-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
  margin-left: -4px;
}

.header-qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
}

.header-qr-id {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.1;
  font-weight: 700;
  font-family: monospace;
  letter-spacing: 1px;
  text-align: center;
}

.codes-right {
  margin-top: 8px !important;
  gap: 0;
}

.barcode-small {
  display: block;
  overflow: visible;

  svg {
    display: block !important;
    width: 420px !important;
    max-width: 420px !important;
    height: 138px !important;
    overflow: visible !important;
  }
}

.footer-qr-wrap {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  flex: 0 0 auto;
}

.footer-barcode-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  margin-left: 4px;
  flex: 0 0 auto;
}

.footer-barcode {
  display: block;
  width: 250px !important;
  max-width: 250px !important;
  height: 104px !important;
  overflow: visible;

  svg {
    display: block !important;
    width: 250px !important;
    max-width: 250px !important;
    height: 104px !important;
    overflow: visible !important;
  }
}

.footer-qr canvas,
.footer-qr img {
  display: block;
  width: 90px;
  height: 90px;
}

.logo-top {
  display: block;
  width: 100px;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
}

.dn-header-logo-center {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
}

.dn-header-codes-right {
  flex: 0 0 auto;
  text-align: end;
}

.status-banner {
  margin: 6px 16px 8px;
  padding: 7px 12px;
  border: 2px solid #1d1d1b;
  border-radius: 4px;
  text-align: center;
  line-height: 1.1;
  background: #fff;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.status-banner--exchange {
  border-color: #EA2F2F;
  background: #fff5f5;
  font-size: 26px;
}

.status-banner--exchange strong {
  color: #EA2F2F;
}

.status-banner--fragile {
  border-color: #bc2b18;
  background: #fff1e8;
  font-size: 32px;
}

.status-banner--fragile strong {
  color: #bc2b18;
}

@media (max-width: 768px) {
  .footer-codes-wrap {
    align-items: flex-start;
  }

  .footer-qr-wrap {
    align-items: flex-start;
  }
}
</style>

<style lang="scss">
@media print {
  @page {
    size: A4 portrait;
    margin: 5mm;
  }

  html,
  body {
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: transparent !important;
  }

  body {
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }

  nav.header-navbar,
  .main-menu,
  .content-header,
  .header-navbar-shadow,
  .customizer-toggle,
  footer.footer,
  .invoice-actions,
  .b-overlay {
    display: none !important;
  }

  .content.app-content,
  .content-wrapper,
  .b-overlay-wrap {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: auto !important;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
    border: none !important;
  }

  .invoice-preview-wrapper {
    --delivery-note-page-height: 287mm;
    width: 100% !important;
    margin: 0 !important;
    padding: 0.5mm 0 0 !important;
  }

  .invoice-preview-wrapper .row.invoice-preview,
  .invoice-preview-wrapper .row.invoice-preview > [class*="col-"] {
    width: 100% !important;
    max-width: 100% !important;
    -webkit-flex: 0 0 100% !important;
    flex: 0 0 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .invoice-preview-card.pagebreak {
    display: -webkit-flex !important;
    display: flex !important;
    -webkit-flex-direction: column !important;
    flex-direction: column !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: var(--delivery-note-page-height) !important;
    box-sizing: border-box !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 5mm 0 7mm !important;
    -webkit-column-break-inside: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .invoice-preview-card.pagebreak:not(:last-child) {
    border-bottom: none !important;
    -webkit-column-break-after: always !important;
    page-break-after: always !important;
    break-after: page !important;
  }

  .invoice-preview-card.pagebreak:nth-child(2n):not(:last-child) {
    -webkit-column-break-after: always !important;
    page-break-after: always !important;
    break-after: page !important;
  }

  .invoice-preview-card.pagebreak:last-child {
    border-bottom: none !important;
    -webkit-column-break-after: auto !important;
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .delivery-note-header {
    margin: 0 5mm 5mm !important;
    gap: 5mm !important;
    align-items: flex-start !important;
  }

  .delivery-note-header > * {
    min-width: 0 !important;
  }

  .delivery-note-header .invoice-preview {
    flex: 1 1 auto !important;
  }

  .delivery-note-company {
    flex: 0 0 auto !important;
    overflow: visible !important;
  }

  .dn-header-logo {
    width: 28mm !important;
    max-width: 28mm !important;
    height: auto !important;
  }
  .logo-top {
    width: 26mm !important;
    max-width: 26mm !important;
    margin: 0 auto !important;
  }
  .dn-header-logo-center {
    flex: 0 0 auto !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 2mm !important;
  }
  .dn-header-codes-right {
    flex: 0 0 auto !important;
  }
  .dn-header-right-codes {
    align-items: flex-end !important;
  }
  .dn-header-codes {
    gap: 4px !important;
    margin-top: 3px !important;
  }
  .header-qr-id {
    margin-top: 4px !important;
    font-size: 16px !important;
    font-weight: 800 !important;
    line-height: 1 !important;
    letter-spacing: 0.8px !important;
  }
  .dn-header-codes .barcode-text-fallback {
    font-size: 9px !important;
    letter-spacing: 1px !important;
  }
  .dn-title {
    font-size: 28px !important;
    line-height: 1.08 !important;
    margin: 0 0 2mm 0 !important;
  }
  .dn-title .invoice-number {
    font-size: 22px !important;
  }
  .dn-date {
    font-size: 16px !important;
    line-height: 1.15 !important;
    margin: 0 0 2.5mm !important;
  }
  .delivery-note-header .h4,
  .company-meta .h4 {
    font-size: 15px !important;
    line-height: 1.35 !important;
    margin: 0 0 1.6mm !important;
    white-space: nowrap !important;
  }

  .header-barcode-wrap {
    margin-left: -34px !important;
    transform: translateX(-10px) !important;
  }

  .codes-right {
    margin-top: 2mm !important;
    gap: 0 !important;
    flex-wrap: nowrap !important;
    align-items: flex-start !important;
  }

  .header-barcode svg,
  .header-barcode canvas,
  .barcode-small svg {
    display: block !important;
    width: 380px !important;
    max-width: 380px !important;
    height: 126px !important;
    margin-left: 0 !important;
    overflow: visible !important;
  }

  .header-qr-wrap canvas,
  .header-qr-wrap img {
    width: 106px !important;
    height: 106px !important;
  }

  .invoice-preview-card .dn-info-row {
    margin: 0 5mm 5mm !important;
  }

  .dn-info-row {
    gap: 6px !important;
  }

  .dn-info-col {
    flex: 1 1 45% !important;
    min-width: 0 !important;
  }

  .client-info-box {
    font-size: 22px !important;
    line-height: 1.35 !important;
    min-height: 38mm !important;
  }

  .supplier-info-box {
    font-size: 17px !important;
    line-height: 1.35 !important;
    min-height: 38mm !important;
  }

  .client-info-box .card-text,
  .supplier-info-box .card-text {
    margin-bottom: 1mm !important;
  }

  .status-banner {
    margin: 0 5mm 3mm !important;
    padding: 2mm 3mm !important;
    border: 0.8mm solid #1d1d1b !important;
    border-radius: 1mm !important;
    text-align: center !important;
    line-height: 1.08 !important;
    background: #fff !important;
    letter-spacing: 1.2px !important;
    text-transform: uppercase !important;
    -webkit-column-break-inside: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  .status-banner--exchange {
    border-color: #EA2F2F !important;
    background: #fff5f5 !important;
    font-size: 28px !important;
  }
  .status-banner--exchange strong {
    color: #EA2F2F !important;
  }
  .status-banner--fragile {
    border-color: #bc2b18 !important;
    background: #fff1e8 !important;
    font-size: 34px !important;
  }
  .status-banner--fragile strong {
    color: #bc2b18 !important;
  }

  .delivery-note-table {
    flex: 0 0 auto !important;
    min-height: 0 !important;
    margin: 0 5mm 5mm 5mm !important;
    padding-bottom: 0 !important;
    overflow: visible !important;
  }

  .delivery-note-table .table,
  .delivery-note-table .table-responsive {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
    overflow: visible !important;
  }

  .ta {
    white-space: nowrap;
    overflow-x: hidden;
    font-size: 18px !important;
    color: black !important;
  }

  table,
  td,
  th {
    padding: 9px 11px !important;
    text-align: left;
    border: 1px solid black;
    font-size: 17px !important;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  .table thead th {
    border-bottom: 2px solid black;
  }

  .dn-after-table-row {
    display: -webkit-flex !important;
    display: flex !important;
    -webkit-justify-content: space-between !important;
    justify-content: space-between !important;
    -webkit-align-items: flex-start !important;
    align-items: flex-start !important;
    gap: 4mm !important;
    margin: 5mm 5mm 0 !important;
    padding: 0 !important;
    -webkit-flex: 0 0 auto !important;
    flex: 0 0 auto !important;
  }

  .dn-after-table-left {
    -webkit-flex: 1 1 auto !important;
    flex: 1 1 auto !important;
    min-width: 0 !important;
  }

  .dn-after-table-right {
    -webkit-flex: 0 0 auto !important;
    flex: 0 0 auto !important;
    -webkit-align-self: flex-start !important;
    align-self: flex-start !important;
  }

  .dn-qr-section {
    margin-top: auto !important;
    margin-left: 5mm !important;
    margin-right: 5mm !important;
    margin-bottom: 0 !important;
    padding: 0 0 10mm !important;
    flex: 0 0 auto !important;
  }

  .dn-comment-line {
    margin: 0 !important;
    font-size: 19px !important;
    line-height: 1.35 !important;
  }

  .dn-comment-text {
    font-size: 18px !important;
  }

  .dn-flags-list {
    display: flex !important;
    flex-direction: column !important;
    gap: 0.5mm !important;
  }

  .dn-flags-list .h4 {
    font-size: 16px !important;
    line-height: 1.3 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .footer-qr-wrap {
    display: -webkit-inline-flex !important;
    display: inline-flex !important;
    -webkit-flex-direction: column !important;
    flex-direction: column !important;
    -webkit-align-items: flex-start !important;
    align-items: flex-start !important;
    gap: 6px !important;
    margin: 0 !important;
    -webkit-align-self: flex-start !important;
    align-self: flex-start !important;
    -webkit-column-break-inside: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .footer-qr canvas,
  .footer-qr img {
    width: 130px !important;
    height: 130px !important;
  }

  .footer-codes-wrap {
    gap: 0 !important;
    flex-wrap: nowrap !important;
    align-items: flex-end !important;
  }

  .footer-barcode-wrap {
    margin-left: 4px !important;
  }

  .footer-barcode svg {
    display: block !important;
    width: 340px !important;
    max-width: 340px !important;
    height: 122px !important;
    overflow: visible !important;
  }

  .footer-barcode {
    width: 340px !important;
    max-width: 340px !important;
    height: 122px !important;
    overflow: visible !important;
  }

  .dn-total-box {
    flex: 0 0 auto !important;
    max-width: 82mm !important;
    margin-left: 0 !important;
    align-self: flex-end !important;
  }

  .dn-total-box table {
    margin: 0 !important;
  }

  .dn-total-box th,
  .dn-price-ttc {
    font-size: 26px !important;
    font-weight: 900 !important;
    text-align: center !important;
    padding: 11px 16px !important;
  }

  .mycolor {
    background-color: #1565C0;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
  .contact {
    background-color: #1565C0;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
  .badge.badge-info {
    background-color: #17a2b8 !important;
    color: white !important;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }

  .invoice-preview-card.dn-print-comfortable .delivery-note-header,
  .invoice-preview-card.dn-print-roomy .delivery-note-header {
    margin-bottom: 5mm !important;
  }

  .invoice-preview-card.dn-print-comfortable .dn-title {
    font-size: 29px !important;
    line-height: 1.08 !important;
  }

  .invoice-preview-card.dn-print-roomy .dn-title {
    font-size: 31px !important;
    line-height: 1.08 !important;
  }

  .invoice-preview-card.dn-print-comfortable .dn-date,
  .invoice-preview-card.dn-print-roomy .dn-date {
    font-size: 16px !important;
    line-height: 1.15 !important;
  }

  .invoice-preview-card.dn-print-comfortable .delivery-note-header .h4 {
    font-size: 16px !important;
    line-height: 1.35 !important;
    margin-bottom: 1.6mm !important;
  }

  .invoice-preview-card.dn-print-roomy .delivery-note-header .h4 {
    font-size: 17px !important;
    line-height: 1.35 !important;
    margin-bottom: 1.8mm !important;
  }

  .invoice-preview-card.dn-print-comfortable .client-info-box,
  .invoice-preview-card.dn-print-roomy .client-info-box {
    font-size: 23px !important;
    line-height: 1.35 !important;
  }

  .invoice-preview-card.dn-print-roomy .client-info-box {
    min-height: 42mm !important;
  }

  .invoice-preview-card.dn-print-comfortable .supplier-info-box,
  .invoice-preview-card.dn-print-roomy .supplier-info-box {
    font-size: 18px !important;
    line-height: 1.35 !important;
  }

  .invoice-preview-card.dn-print-roomy .supplier-info-box {
    min-height: 42mm !important;
  }

  .invoice-preview-card.dn-print-comfortable .dn-agence-line,
  .invoice-preview-card.dn-print-roomy .dn-agence-line {
    font-size: 17px !important;
    padding: 2mm 3mm !important;
    margin-bottom: 4mm !important;
  }

  .invoice-preview-card.dn-print-comfortable .delivery-note-table td,
  .invoice-preview-card.dn-print-comfortable .delivery-note-table th {
    font-size: 18px !important;
    padding: 9px 11px !important;
  }

  .invoice-preview-card.dn-print-roomy .delivery-note-table td,
  .invoice-preview-card.dn-print-roomy .delivery-note-table th {
    font-size: 19px !important;
    padding: 10px 12px !important;
  }

  .invoice-preview-card.dn-print-comfortable .dn-after-table-row,
  .invoice-preview-card.dn-print-roomy .dn-after-table-row {
    margin-top: 5mm !important;
  }

  .invoice-preview-card.dn-print-comfortable .dn-comment-line,
  .invoice-preview-card.dn-print-roomy .dn-comment-line {
    font-size: 20px !important;
    line-height: 1.35 !important;
  }

  .invoice-preview-card.dn-print-comfortable .dn-comment-text,
  .invoice-preview-card.dn-print-roomy .dn-comment-text {
    font-size: 19px !important;
  }

  .invoice-preview-card.dn-print-comfortable .dn-flags-list .h4,
  .invoice-preview-card.dn-print-roomy .dn-flags-list .h4 {
    font-size: 17px !important;
    line-height: 1.3 !important;
  }

  .invoice-preview-card.dn-print-comfortable .dn-total-box th,
  .invoice-preview-card.dn-print-comfortable .dn-price-ttc {
    font-size: 27px !important;
    padding: 11px 16px !important;
  }

  .invoice-preview-card.dn-print-roomy .dn-total-box th,
  .invoice-preview-card.dn-print-roomy .dn-price-ttc {
    font-size: 29px !important;
    padding: 12px 18px !important;
  }

  .invoice-preview-card.dn-print-roomy .dn-qr-section {
    padding-bottom: 12mm !important;
  }
}
</style>
